import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { IconChevronDown } from '@tabler/icons-react';
import { WP_ICON_NAV } from '../icons/constants.js';
import { cx } from '../utils/cx.js';
import { usePresence } from '../utils/usePresence.js';
import { overlayPresenceProps } from '../hooks/mistRail.js';
import clusterStyles from './NavCluster.module.css';
import styles from './NavIconFlyout.module.css';
import { Pressable } from '../primitives/Pressable.js';
import { ProximityScope } from '../primitives/ProximityHighlight.js';

const OPEN_MS = 80;
const CLOSE_MS = 220;
/** Panel enter/exit transition (matches CSS). */
const PANEL_MS = 160;

export type NavIconFlyoutProps = {
  icon: ReactNode;
  /** Shown in aria-label / title. */
  label: string;
  count?: number;
  /** Unread items — pill badge + soft pulse when panel closed. */
  unseenCount?: number;
  /** `aria-label` override (defaults to label + count). */
  menuLabel?: string;
  /** Wider/taller panel for drill-down content (e.g. inbox job detail). */
  panelVariant?: 'default' | 'expanded';
  /** Panel stays open until false (e.g. inbox drill-down); ignores hover/blur close. */
  keepOpen?: boolean;
  /** Called before the panel closes (reset nested drill-down, etc.). */
  onRequestClose?: () => void;
  /** Visible label beside icon (e.g. mobile nav drawer row). */
  triggerDetail?: ReactNode;
  /** Full-width row trigger (mobile drawer). */
  triggerLayout?: 'compact' | 'row';
  /**
   * `chrome` — bordered chip (default).
   * `ghost` — quiet icon control for `NavUtilityCluster` (no chevron).
   */
  triggerVariant?: 'chrome' | 'ghost';
  /**
   * `default` — padded panel (simple menus).
   * `flush` — no pad; child owns edge-to-edge layout (inbox / messages).
   */
  panelPadding?: 'default' | 'flush';
  children: ReactNode;
};

export function NavIconFlyout({
  icon,
  label,
  count,
  unseenCount,
  menuLabel,
  panelVariant = 'default',
  keepOpen = false,
  onRequestClose,
  triggerDetail,
  triggerLayout = 'compact',
  triggerVariant = 'chrome',
  panelPadding = 'default',
  children,
}: NavIconFlyoutProps) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const openT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [portalPos, setPortalPos] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  const { mounted, visible } = usePresence(open, { ms: PANEL_MS, panelRef });

  const isExpanded = panelVariant === 'expanded';
  const isFlush = panelPadding === 'flush';
  const portalMinWidth = isExpanded ? 560 : 360;
  const portalMaxWidth = isExpanded ? 580 : 448;
  const portalEstimatedHeight = isExpanded ? 760 : 480;

  const clearTimers = useCallback(() => {
    if (openT.current) clearTimeout(openT.current);
    if (closeT.current) clearTimeout(closeT.current);
    openT.current = null;
    closeT.current = null;
  }, []);

  const scheduleOpen = useCallback(() => {
    clearTimers();
    openT.current = setTimeout(() => setOpen(true), OPEN_MS);
  }, [clearTimers]);

  const scheduleClose = useCallback(() => {
    if (pinned) return;
    clearTimers();
    closeT.current = setTimeout(() => setOpen(false), CLOSE_MS);
  }, [clearTimers, pinned]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (!keepOpen) return;
    clearTimers();
    setPinned(false);
    setOpen(true);
  }, [keepOpen, clearTimers]);

  const requestClose = useCallback(() => {
    if (keepOpen) return;
    scheduleClose();
  }, [keepOpen, scheduleClose]);

  const closePanel = useCallback(() => {
    onRequestClose?.();
    clearTimers();
    setPinned(false);
    setOpen(false);
  }, [onRequestClose, clearTimers]);

  const pinOpen = useCallback(() => {
    clearTimers();
    setPinned(true);
    setOpen(true);
  }, [clearTimers]);

  const updatePortalPos = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const narrow = window.matchMedia('(max-width: 47.99em)').matches;
    if (narrow) {
      setPortalPos({ top: 0, left: 0, width: window.innerWidth });
      return;
    }
    const r = el.getBoundingClientRect();
    const width = Math.min(portalMaxWidth, Math.max(portalMinWidth, window.innerWidth - 16));
    let left = r.right - width;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    const est = Math.min(portalEstimatedHeight, window.innerHeight - 16);
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const spaceAbove = r.top - 8;
    const preferAbove = spaceBelow < est && spaceAbove > spaceBelow;
    const top = preferAbove ? Math.max(8, r.top - est - 6) : r.bottom + 6;
    setPortalPos({ top, left, width });
  }, [portalMinWidth, portalMaxWidth, portalEstimatedHeight]);

  // Keyed on `mounted` so the panel keeps its position while it animates out.
  useLayoutEffect(() => {
    if (!mounted) {
      setPortalPos(null);
      return;
    }
    updatePortalPos();
  }, [mounted, updatePortalPos, panelVariant]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => updatePortalPos();
    window.addEventListener('resize', onMove);
    document.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      document.removeEventListener('scroll', onMove, true);
    };
  }, [open, updatePortalPos]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      closePanel();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, closePanel]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (keepOpen) {
        onRequestClose?.();
        return;
      }
      closePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, keepOpen, onRequestClose, closePanel]);

  const hasCount = typeof count === 'number';
  const hasUnseen = typeof unseenCount === 'number' && unseenCount > 0;
  const a11yLabel =
    menuLabel ??
    (hasUnseen
      ? `${label} — ${unseenCount} non lue${unseenCount > 1 ? 's' : ''}`
      : hasCount
        ? `${label} — ${count}`
        : label);
  const showUnseenPulse = hasUnseen && !open;
  const ni = WP_ICON_NAV;
  const isRowTrigger = triggerLayout === 'row';
  const isGhost = triggerVariant === 'ghost';

  const handleTriggerClick = () => {
    if (open) {
      if (keepOpen) {
        onRequestClose?.();
        return;
      }
      if (pinned) {
        closePanel();
        return;
      }
      pinOpen();
      return;
    }
    pinOpen();
  };

  const panelStyle: CSSProperties | undefined = portalPos
    ? { top: portalPos.top, left: portalPos.left, width: portalPos.width }
    : undefined;

  const panelNode =
    mounted && portalPos ? (
      <div
        ref={panelRef}
        className={cx(
          styles.panel,
          styles.panelPortal,
          isExpanded && styles.panelExpanded,
          isFlush && styles.panelFlush,
          visible && styles.panelVisible,
        )}
        style={panelStyle}
        role="dialog"
        aria-label={label}
        {...overlayPresenceProps(visible)}
        onMouseEnter={pinOpen}
        onMouseLeave={requestClose}
      >
        <ProximityScope
          axis="y"
          className={cx(styles.panelInner, isFlush && styles.panelInnerFlush)}
        >
          {children}
        </ProximityScope>
      </div>
    ) : null;

  return (
    <div
      ref={anchorRef}
      className={cx(clusterStyles.wrap, styles.anchor, open && clusterStyles.open)}
      data-wp-proximity-nav=""
      onMouseEnter={scheduleOpen}
      onMouseLeave={requestClose}
      onFocusCapture={() => {
        if (!pinned) scheduleOpen();
      }}
      onBlurCapture={(e) => {
        if (keepOpen) return;
        if (pinned) return;
        const t = e.relatedTarget as Node | null;
        if (t && e.currentTarget.contains(t)) return;
        if (t && panelRef.current?.contains(t)) return;
        if (
          !t &&
          (e.currentTarget.contains(document.activeElement) ||
            panelRef.current?.contains(document.activeElement))
        ) {
          return;
        }
        requestClose();
      }}
    >
      <Pressable
        type="button"
        className={cx(
          clusterStyles.trigger,
          styles.triggerFlyout,
          isGhost && styles.triggerGhost,
          isRowTrigger && styles.triggerRow,
          showUnseenPulse && styles.triggerUnseen,
        )}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={a11yLabel}
        title={a11yLabel}
        onClick={handleTriggerClick}
      >
        <span className={cx(styles.iconWrap, showUnseenPulse && styles.iconWrapPulse)}>
          {icon}
        </span>
        {triggerDetail ? <span className={styles.triggerDetail}>{triggerDetail}</span> : null}
        {hasUnseen ? (
          <span className={styles.unseenBadge} aria-hidden>
            {unseenCount > 99 ? '99+' : unseenCount}
          </span>
        ) : hasCount ? (
          <>
            {isGhost ? null : <span className={clusterStyles.triggerSep} aria-hidden />}
            <span className={clusterStyles.triggerCount}>{count}</span>
          </>
        ) : null}
        {isGhost ? null : (
          <IconChevronDown
            {...ni}
            size={12}
            className={cx(clusterStyles.chev, isRowTrigger && styles.triggerRowChev)}
            stroke={1.5}
            aria-hidden
          />
        )}
      </Pressable>
      {typeof document !== 'undefined' && panelNode ? createPortal(panelNode, document.body) : null}
    </div>
  );
}
